import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Printer, DownloadSimple } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { InvoicePrintout } from "@/components/InvoicePrintout";
import { PortalPayOptions } from "@/components/portal/PortalPayOptions";
import { portalApi, portalMoney } from "@/lib/portalApi";

export default function PortalInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);

  const load = useCallback(async () => {
    const { data } = await portalApi.get(`/invoices/${id}`);
    setInv(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadReceipt = async () => {
    try {
      const { data } = await portalApi.get(`/invoices/${id}/receipt.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt-${inv.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Receipt is not available yet");
    }
  };

  if (!inv)
    return (
      <PortalShell back="/portal/home">
        <div className="mt-10 h-10 w-10 animate-pulse rounded-full bg-slate-100" />
      </PortalShell>
    );

  return (
    <PortalShell
      back={
        inv.project_id ? `/portal/projects/${inv.project_id}` : "/portal/home"
      }
      title={`Invoice ${inv.code}`}
      status={{
        label: inv.status,
        color: {
          Draft: "bg-slate-400",
          Sent: "bg-blue-400",
          "Partly paid": "bg-amber-400",
          Paid: "bg-emerald-400",
          Void: "bg-slate-400",
        }[inv.status],
      }}
      subtitle={[inv.title, inv.job_address].filter(Boolean).join(" · ")}
    >
      <div className="mt-6 space-y-5" data-testid="portal-invoice-page">
        <div className="no-print flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Balance due
            </div>
            <div
              className="font-mono-data text-3xl font-semibold text-slate-900"
              data-testid="portal-invoice-balance"
            >
              {portalMoney(inv.balance)}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {inv.status} · {portalMoney(inv.paid)} paid of{" "}
              {portalMoney(inv.amount)}
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
            <button
              type="button"
              data-testid="portal-invoice-print"
              onClick={() => window.print()}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-500 transition-colors duration-150 hover:bg-slate-100 sm:flex-none"
            >
              <Printer size={15} weight="bold" /> Print
            </button>
            {inv.paid > 0 && (
              <button
                type="button"
                data-testid="portal-invoice-receipt"
                onClick={downloadReceipt}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-500 transition-colors duration-150 hover:bg-slate-100 sm:flex-none"
              >
                <DownloadSimple size={15} weight="bold" /> Receipt (PDF)
              </button>
            )}
          </div>
          <PortalPayOptions inv={inv} onChanged={load} />
        </div>

        <div
          className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white"
          data-testid="portal-invoice-document"
        >
          <InvoicePrintout invoice={inv} template={inv.template} />
        </div>
      </div>
    </PortalShell>
  );
}
